/**
 * VOICE PROFILE LEVELS (#4740 VOICE-5).
 *
 * Every client's voice configuration carries an explicit maturity level. The level says
 * how much of the voice stack that client has actually had authored for them — nothing
 * more. It is NOT a stage: the founder's frame owns the word "stages" for the one-to-ten
 * onboarding sequence, and a voice profile level lives INSIDE whichever stage a client is
 * in. The two vocabularies must never be conflated.
 *
 * LAW — A LEVEL ONLY ENRICHES. Declaring a level never gates, blocks, degrades, or
 * reroutes an answer. There is no branch anywhere on the answer path that reads a level
 * and does less because it is low. A client at the first level gets the same governed
 * answer machinery as a client at the third; the only difference is how much
 * client-specific voice material the composer has to draw on.
 *
 * WHY IT IS DECLARED. Ordinary run metadata can expose the level so downstream
 * evaluation can treat absent or unverifiable maturity as not applicable. The declaration
 * itself never makes execution conditional.
 *
 * SINGLE SOURCE. This module is the ONLY authority for the level vocabulary and for what
 * each level comprises. Nothing re-types the union, re-lists the components, or hard-codes
 * a level string against its own private list.
 */

/**
 * The closed level vocabulary, ordered from least to most authored voice material.
 * Ordering is load-bearing: index position IS the maturity comparison.
 */
export const VOICE_PROFILE_LEVELS = ['L1', 'L2', 'L3'] as const;

export type VoiceProfileLevel = (typeof VOICE_PROFILE_LEVELS)[number];

/**
 * The named things a voice profile can be composed of. Names only — the prose that
 * describes each one lives in the artifact that implements it, never duplicated here.
 */
export const VOICE_PROFILE_LEVEL_COMPONENTS = [
  'identity',
  'industry',
  'personaSet',
  'terminologyRegistry',
  'narrationPreferences',
  'houseStyleCalibration',
] as const;

export type VoiceProfileLevelComponent = (typeof VOICE_PROFILE_LEVEL_COMPONENTS)[number];

export type VoiceProfileLevelDescriptor = Readonly<{
  level: VoiceProfileLevel;
  /** Cumulative: each level comprises everything the level below it comprises. */
  comprises: readonly VoiceProfileLevelComponent[];
}>;

const L1_COMPONENTS = ['identity', 'industry', 'personaSet'] as const;
const L2_COMPONENTS = [...L1_COMPONENTS, 'terminologyRegistry', 'narrationPreferences'] as const;
const L3_COMPONENTS = [...L2_COMPONENTS, 'houseStyleCalibration'] as const;

export const VOICE_PROFILE_LEVEL_DESCRIPTORS: Readonly<
  Record<VoiceProfileLevel, VoiceProfileLevelDescriptor>
> = Object.freeze({
  L1: Object.freeze({ level: 'L1', comprises: L1_COMPONENTS }),
  L2: Object.freeze({ level: 'L2', comprises: L2_COMPONENTS }),
  L3: Object.freeze({ level: 'L3', comprises: L3_COMPONENTS }),
});

const VOICE_PROFILE_LEVEL_SET: ReadonlySet<string> = new Set<string>(VOICE_PROFILE_LEVELS);

/** Membership test for the closed vocabulary. Accepts unknown so config loaders can use it. */
export function isVoiceProfileLevel(value: unknown): value is VoiceProfileLevel {
  return typeof value === 'string' && VOICE_PROFILE_LEVEL_SET.has(value);
}

/**
 * Narrows an unvalidated config value to a level, or null when it is absent or outside
 * the closed vocabulary. Deliberately strict: no trimming, no case folding, no aliasing.
 * A declaration is either exactly one of the published tokens or it is not a declaration.
 */
export function normalizeVoiceProfileLevel(value: unknown): VoiceProfileLevel | null {
  return isVoiceProfileLevel(value) ? value : null;
}

/** What the given level comprises. Cumulative — see the descriptors above. */
export function voiceProfileLevelComprises(
  level: VoiceProfileLevel
): readonly VoiceProfileLevelComponent[] {
  return VOICE_PROFILE_LEVEL_DESCRIPTORS[level].comprises;
}

/**
 * True when a level's profile includes the named component. Used by evaluation to decide
 * whether a component is even in scope for a client — never to gate an answer.
 */
export function voiceProfileLevelIncludes(
  level: VoiceProfileLevel,
  component: VoiceProfileLevelComponent
): boolean {
  return voiceProfileLevelComprises(level).includes(component);
}
